import { useState, type FormEvent } from "react";

import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface BlogNewsletterSignupProps {
  /**
   * Slug of the post the user is reading when they sign up. Stored for
   * attribution; safe to omit on the /blog index page or if unknown.
   */
  sourcePostSlug?: string;
}

/**
 * Email-capture CTA shown on every blog post. Writes directly to
 * blog_newsletter_subscribers via the public RLS policy (anon INSERT
 * allowed, admin-only SELECT). Already-subscribed emails are silently
 * accepted as success, no need to surface unique-constraint errors to a
 * reader who's just trying to subscribe.
 */
export function BlogNewsletterSignup({ sourcePostSlug }: BlogNewsletterSignupProps) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || submitting) return;

    setSubmitting(true);
    try {
      const { error } = await supabase.from("blog_newsletter_subscribers").insert({
        email: trimmed,
        source_post_slug: sourcePostSlug ?? null,
        status: "active",
      });

      // 23505 = unique_violation. Treat re-subscribes as success.
      if (error && error.code !== "23505") {
        throw error;
      }

      setSubmitted(true);
      setEmail("");
      toast.success("You're in. We'll only email when there's something worth your time.");
    } catch (err) {
      console.error("[newsletter] signup failed:", err);
      toast.error("Something went wrong. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-cream rounded-2xl p-8 text-center border border-charcoal/10">
        <p className="text-sm font-semibold text-navy">
          You're in. We'll be in touch.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-cream rounded-2xl p-8 border border-charcoal/10">
      <h3 className="font-heading text-xl text-navy mb-2 text-center">
        New posts, in your inbox.
      </h3>
      <p className="text-sm text-charcoal/60 text-center mb-5">
        One email when we publish a new guide. No spam, no lists you can't escape.
      </p>
      <form
        onSubmit={handleSubmit}
        className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto"
      >
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@yourcompany.com"
          aria-label="Email address"
          autoComplete="email"
          /* MS162: RFC 5321 + matches MS157 DB cap on
             blog_newsletter_subscribers.email (320). */
          maxLength={320}
          /* MS204: text-base on mobile to dodge iOS Safari auto-
             zoom-on-focus (any input <16px font-size triggers it).
             md:text-sm preserves the inline-form density on desk. */
          className="flex-1 px-4 py-2.5 rounded-full border border-charcoal/15 bg-white text-base md:text-sm text-navy placeholder:text-charcoal/30 focus:outline-none focus:border-coral focus:ring-2 focus:ring-coral/20 transition-colors"
        />
        <button
          type="submit"
          disabled={submitting}
          className="px-6 py-2.5 rounded-full bg-coral text-white font-semibold text-sm hover:bg-coral/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? "…" : "Subscribe"}
        </button>
      </form>
    </div>
  );
}
